import type { AnalyticsPeriodMode } from '@/features/expenses/types/expense.types'
import { deriveDateRange, toDateString } from './expenseUtils'

type SteppedMode = Exclude<AnalyticsPeriodMode, 'range'>

/** 'YYYY-MM-DD' → local midnight. */
function parse(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

/** The anchor moved one week, month or year back (-1) or forward (+1). */
export function shiftAnchor(mode: SteppedMode, anchor: string, step: -1 | 1): string {
  const date = parse(anchor)
  if (mode === 'week') date.setDate(date.getDate() + 7 * step)
  // Day 1 first, so stepping from Jan 31 lands in February, not March.
  else if (mode === 'month') date.setMonth(date.getMonth() + step, 1)
  else date.setFullYear(date.getFullYear() + step, 0, 1)
  return toDateString(date)
}

/** "October 2026", "Oct 5 – 11, 2026" (or across months/years), "2026". */
export function periodLabel(mode: SteppedMode, anchor: string): string {
  if (mode === 'year') return String(parse(anchor).getFullYear())
  if (mode === 'month') {
    return parse(anchor).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }
  const { dateFrom, dateTo } = deriveDateRange('week', anchor)
  const from = parse(dateFrom)
  const to = parse(dateTo)
  const sameYear = from.getFullYear() === to.getFullYear()
  const start = from.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
  const end =
    from.getMonth() === to.getMonth() && sameYear
      ? String(to.getDate())
      : to.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${start} – ${end}, ${to.getFullYear()}`
}

/** Whether the period holding `anchor` is the one holding today. */
export function isCurrentPeriod(mode: SteppedMode, anchor: string, today: string): boolean {
  return deriveDateRange(mode, anchor).dateFrom === deriveDateRange(mode, today).dateFrom
}
